import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Field, TextInput, TextArea, SelectInput, EmptyState } from "@/components/Fields";
import {
  listEmpresas,
  createEmpresa,
  updateEmpresa,
  deleteEmpresa,
  getEnums,
} from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, Search, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  razon_social: "", nif: "", actividad: "", direccion: "",
  telefono: "", email: "", modalidad_preventiva: "", observaciones: "",
};

export default function Empresas() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [modalidades, setModalidades] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = useCallback((s = "") => {
    setLoading(true);
    listEmpresas(s).then((d) => { setItems(d); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); getEnums().then((e) => setModalidades(e.modalidades_preventivas)); }, [load]);

  useEffect(() => {
    if (params.get("nueva") === "1") { openNew(); setParams({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e }); setEditId(e.id); setDialogOpen(true); };
  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  const save = async () => {
    if (!form.razon_social.trim()) return toast.error("La razón social es obligatoria");
    try {
      if (editId) { await updateEmpresa(editId, form); toast.success("Empresa actualizada"); }
      else { await createEmpresa(form); toast.success("Empresa creada"); }
      setDialogOpen(false); load(search);
    } catch { toast.error("No se pudo guardar la empresa"); }
  };

  const confirmDelete = async () => {
    try {
      await deleteEmpresa(toDelete.id);
      toast.success("Empresa eliminada");
      setToDelete(null); load(search);
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <Layout
      title="Empresas"
      subtitle="Gestión de empresas clientes"
      actions={
        <button
          onClick={openNew}
          data-testid="btn-nueva-empresa"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 transition-colors"
        >
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nueva empresa</span>
        </button>
      }
    >
      <div className="relative mb-6 max-w-md">
        <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="empresas-search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
          placeholder="Buscar por razón social, NIF o actividad…"
          className="h-12 w-full border border-slate-300 bg-white rounded-lg pl-10 pr-3 text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Sin empresas todavía"
            description="Crea tu primera empresa para empezar a construir su expediente preventivo."
            action={
              <button
                onClick={openNew}
                data-testid="empty-nueva-empresa"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800"
              >
                <Plus className="w-5 h-5" /> Nueva empresa
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <th className="p-4 font-semibold">Razón social</th>
                  <th className="p-4 font-semibold hidden md:table-cell">NIF</th>
                  <th className="p-4 font-semibold hidden lg:table-cell">Actividad</th>
                  <th className="p-4 font-semibold hidden lg:table-cell">Alta</th>
                  <th className="p-4 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`empresa-row-${e.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/empresas/${e.id}`)}
                  >
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{e.razon_social}</div>
                      <div className="text-xs text-slate-500 md:hidden">{e.nif || "—"}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-600 hidden md:table-cell">{e.nif || "—"}</td>
                    <td className="p-4 text-sm text-slate-600 hidden lg:table-cell">{e.actividad || "—"}</td>
                    <td className="p-4 text-sm text-slate-500 hidden lg:table-cell">{formatFecha(e.created_at)}</td>
                    <td className="p-4" onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(e)}
                          data-testid={`empresa-edit-${e.id}`}
                          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setToDelete(e)}
                          data-testid={`empresa-delete-${e.id}`}
                          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogo crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editId ? "Editar empresa" : "Nueva empresa"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 py-2">
            <div className="sm:col-span-2">
              <Field label="Razón social" required>
                <TextInput testId="f-razon-social" value={form.razon_social} onChange={set("razon_social")} placeholder="Ej. Comercial García S.L." />
              </Field>
            </div>
            <Field label="NIF"><TextInput testId="f-nif" value={form.nif} onChange={set("nif")} placeholder="B12345678" /></Field>
            <Field label="Actividad"><TextInput testId="f-actividad" value={form.actividad} onChange={set("actividad")} placeholder="Comercio minorista" /></Field>
            <div className="sm:col-span-2">
              <Field label="Dirección"><TextInput testId="f-direccion" value={form.direccion} onChange={set("direccion")} /></Field>
            </div>
            <Field label="Teléfono"><TextInput testId="f-telefono" value={form.telefono} onChange={set("telefono")} /></Field>
            <Field label="Correo electrónico"><TextInput testId="f-email" value={form.email} onChange={set("email")} type="email" /></Field>
            <div className="sm:col-span-2">
              <Field label="Modalidad preventiva">
                <SelectInput testId="f-modalidad" value={form.modalidad_preventiva} onChange={set("modalidad_preventiva")} options={modalidades} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Observaciones"><TextArea testId="f-observaciones" value={form.observaciones} onChange={set("observaciones")} /></Field>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="h-12 px-5 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
            <button onClick={save} data-testid="empresa-save" className="h-12 px-6 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{toDelete?.razon_social}</strong> junto con sus centros, trabajadores y expedientes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-empresa" className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
